import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CalendarCheck2, CalendarPlus, Check, Loader2, X } from 'lucide-react';
import { api, ApiError, type PortalTourView } from '@/lib/api';
import { useBrand } from '@/lib/brand';
import { LOCALE, useLang, type PortalLang } from '@/lib/lang';
import { SlotPicker, groupByDay } from './SlotPicker';
import { PORTAL_TOUR_STRINGS, TOUR_STRINGS, timeLabel, type PortalTourStrings, type TourStrings } from './strings';

/**
 * Tours on the signed-in home, for a prospect (the server decides who: never a
 * current resident, never after a tour took place). No tour yet: an
 * invitation to book one, whatever the contract's state (only while tours are
 * published). A tour booked (here or by the office): when and where, with
 * "change time" and "cancel" in a bottom sheet. While tours are not published
 * the card is read only: cancel stays, there are no times to move to. In the
 * prospect's language (Hebrew, Arabic, English); its actions send it along so
 * the tour emails follow it.
 */
type Visible = Exclude<PortalTourView, { state: 'hidden' }>;
type Sheet = null | 'book' | 'move' | 'cancel';

/** The card's words and the shared tour words, in the portal's language. */
/** The card's language when its page sets one (a resident's Hebrew home), else the chosen one. */
const CardLang = createContext<PortalLang | null>(null);

function useTourWords(): { lang: PortalLang; S: TourStrings; P: PortalTourStrings } {
  const forced = useContext(CardLang);
  const chosen = useLang((s) => s.lang);
  const lang = forced ?? chosen;
  return { lang, S: TOUR_STRINGS[lang], P: PORTAL_TOUR_STRINGS[lang] };
}

const fmt = (iso: string, zone: string, o: Intl.DateTimeFormatOptions, lang: PortalLang = 'he') =>
  new Intl.DateTimeFormat(LOCALE[lang], { timeZone: zone, ...o }).format(new Date(iso));
/** Cancel is offered when the server says so (an older API: whenever changes are). */
const cancellable = (t: { canChange: boolean; canCancel?: boolean }) => t.canCancel ?? t.canChange;

/** "יום חמישי, 5 בנובמבר" / "الخميس، 5 نوفمبر". */
export const tourDayLong = (iso: string, zone: string, lang: PortalLang = 'he') =>
  fmt(iso, zone, { weekday: 'long', day: 'numeric', month: 'long' }, lang);

interface CardProps {
  /** Tells home what is showing, so it can keep one primary action. */
  onState?: (state: PortalTourView['state'] | 'error') => void;
  /** `welcome`: before registering, the invitation is a quiet second option under "let's start". */
  variant?: 'home' | 'welcome';
}

/** `lang`: the page's language (home passes Hebrew for a resident who registers); else the chosen one. */
export function PortalTourCard({ lang, ...props }: CardProps & { lang?: PortalLang }) {
  return (
    <CardLang.Provider value={lang ?? null}>
      <TourCard {...props} />
    </CardLang.Provider>
  );
}

function TourCard({ onState, variant = 'home' }: CardProps) {
  const brand = useBrand((s) => s.brand);
  const { P } = useTourWords();
  const [view, setView] = useState<PortalTourView | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [justBooked, setJustBooked] = useState<string | null>(null);
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  useEffect(() => {
    let alive = true;
    setFailed(false);
    api
      .myTour()
      .then((v) => {
        if (!alive) return;
        // An unknown answer (an older API, the office's preview) shows nothing.
        setView(v && (v.state === 'invite' || v.state === 'upcoming') ? v : { state: 'hidden' });
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [attempt]);

  const state = failed ? 'error' : (view?.state ?? null);
  useEffect(() => {
    if (state) onStateRef.current?.(state);
  }, [state]);

  // Not knowing whether tours are offered is not worth an error box on home:
  // tours are an invitation, not a task. A quiet retry line only.
  if (failed) {
    return (
      <p className="mt-4 text-[16px] text-slate-500" role="status">
        {P.checkFailed}{' '}
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="min-h-[48px] font-semibold text-brand-700"
        >
          {P.retry}
        </button>
      </p>
    );
  }
  if (!view)
    return variant === 'home' ? <div className="mt-4 h-[120px] w-full animate-pulse rounded-2xl bg-slate-100" /> : null;
  if (view.state === 'hidden') return null;

  const done = (next: PortalTourView, message: string | null) => {
    setSheet(null);
    setView(next.state === 'invite' || next.state === 'upcoming' ? next : { state: 'hidden' });
    if (message) {
      navigator.vibrate?.([30, 50, 30]);
      setJustBooked(message);
      window.setTimeout(() => setJustBooked(null), 4000);
    }
  };

  return (
    <>
      {view.state === 'invite' ? (
        variant === 'welcome' ? (
          <button
            type="button"
            onClick={() => setSheet('book')}
            className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl border-2 border-brand-500 bg-white px-4 text-[16px] font-semibold text-brand-700"
          >
            <CalendarPlus className="h-5 w-5" aria-hidden /> {P.welcomeBook}
          </button>
        ) : (
          <Invite
            heroUrl={brand?.heroImageUrl ?? null}
            heroIsRender={!!brand?.heroIsRender}
            onBook={() => setSheet('book')}
          />
        )
      ) : (
        <Upcoming
          view={view}
          place={view.buildingName ?? brand?.displayName ?? null}
          justBooked={justBooked}
          onMove={() => setSheet('move')}
          onCancel={() => setSheet('cancel')}
        />
      )}
      {sheet && (
        <TourSheet
          mode={sheet}
          view={view}
          onClose={() => setSheet(null)}
          onDone={done}
          onStale={(v) => setView(v.state === 'hidden' ? { state: 'hidden' } : v)}
        />
      )}
    </>
  );
}

function Invite({
  heroUrl,
  heroIsRender,
  onBook,
}: {
  heroUrl: string | null;
  heroIsRender: boolean;
  onBook: () => void;
}) {
  const { P } = useTourWords();
  return (
    <section
      className="mt-4 overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-sm"
      aria-labelledby="tour-invite-title"
      data-testid="tour-invite"
    >
      {heroUrl && (
        <div className="relative h-32">
          <img src={heroUrl} alt="" className="h-full w-full object-cover" />
          {heroIsRender && (
            <span className="absolute end-2.5 top-2.5 rounded-md bg-black/60 px-2 py-0.5 text-[14px] text-white">
              {P.render}
            </span>
          )}
        </div>
      )}
      <div className={'p-4 ' + (heroUrl ? '' : 'bg-brand-50')}>
        <h2 id="tour-invite-title" className="text-[20px] font-bold leading-snug text-brand-900">
          {P.inviteTitle}
        </h2>
        <p className="mt-1 text-[16px] leading-relaxed text-slate-600">{P.inviteBody}</p>
        <button
          type="button"
          onClick={onBook}
          className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[17px] font-semibold text-white shadow-md active:scale-[0.99]"
        >
          <CalendarPlus className="h-5 w-5" aria-hidden /> {P.book}
        </button>
      </div>
    </section>
  );
}

function Upcoming({
  view,
  place,
  justBooked,
  onMove,
  onCancel,
}: {
  view: Visible;
  place: string | null;
  justBooked: string | null;
  onMove: () => void;
  onCancel: () => void;
}) {
  const tour = view.tour!;
  const z = view.timeZone;
  const { lang, S, P } = useTourWords();
  return (
    <section
      className="mt-4 rounded-2xl border border-slate-200 bg-white p-4"
      aria-labelledby="tour-upcoming-title"
      data-testid="tour-upcoming"
    >
      <h2 id="tour-upcoming-title" className="flex items-center gap-2 text-[17px] font-bold text-brand-900">
        {justBooked ? (
          <span className="flex items-center gap-2 text-done" role="status">
            <Check className="h-5 w-5 animate-[pop_.4s_ease-out]" aria-hidden /> {justBooked}
          </span>
        ) : (
          <>
            <CalendarCheck2 className="h-5 w-5 text-brand-600" aria-hidden /> {P.yourTour}
          </>
        )}
      </h2>
      <div className="mt-3 flex items-center gap-4">
        {/* The day as a small calendar leaf: the date is the thing to remember. */}
        <div
          className="flex w-[68px] shrink-0 flex-col items-center overflow-hidden rounded-xl border border-brand-200 text-center"
          aria-hidden
        >
          <span className="w-full bg-brand-600 py-0.5 text-[14px] font-semibold text-white">
            {fmt(tour.start, z, { weekday: 'short' }, lang)}
          </span>
          <span className="pt-1 text-[26px] font-bold leading-none text-brand-900">
            {fmt(tour.start, z, { day: 'numeric' }, lang)}
          </span>
          <span className="pb-1.5 text-[14px] text-slate-600">{fmt(tour.start, z, { month: 'short' }, lang)}</span>
        </div>
        <div className="min-w-0">
          <p className="text-[17px] font-semibold text-brand-900">{tourDayLong(tour.start, z, lang)}</p>
          <p className="text-[16px] text-slate-700">
            {P.at} <span dir="ltr">{timeLabel(tour.start, z, S)}</span>
          </p>
          {(place || tour.unitTypeName) && (
            <p className="text-[16px] text-slate-600">{[place, tour.unitTypeName].filter(Boolean).join(P.comma)}</p>
          )}
        </div>
      </div>
      {!tour.canChange && cancellable(tour) && (
        <>
          <p className="mt-3 text-[16px] text-slate-600">{P.officeToChange}</p>
          <button
            type="button"
            onClick={onCancel}
            className="mt-3 min-h-[48px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[16px] font-semibold text-slate-700"
          >
            {P.cancel}
          </button>
        </>
      )}
      {tour.canChange && (
        <div className={'mt-4 grid gap-2 ' + (cancellable(tour) ? 'grid-cols-2' : 'grid-cols-1')}>
          <button
            type="button"
            onClick={onMove}
            className="min-h-[48px] rounded-xl border border-brand-300 bg-white px-3 text-[16px] font-semibold text-brand-700"
          >
            {P.move}
          </button>
          {cancellable(tour) && (
            <button
              type="button"
              onClick={onCancel}
              className="min-h-[48px] rounded-xl border border-slate-200 bg-white px-3 text-[16px] font-semibold text-slate-700"
            >
              {P.cancel}
            </button>
          )}
        </div>
      )}
      <style>{`@keyframes pop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}`}</style>
    </section>
  );
}

function TourSheet({
  mode,
  view,
  onClose,
  onDone,
  onStale,
}: {
  mode: Exclude<Sheet, null>;
  view: Visible;
  onClose: () => void;
  onDone: (v: PortalTourView, message: string | null) => void;
  onStale: (v: PortalTourView) => void;
}) {
  const days = useMemo(() => groupByDay(view.slots), [view.slots]);
  const current = view.tour?.start ?? null;
  const firstFree = days.find((d) => d.anyFree)?.date ?? null;
  const [day, setDay] = useState<string | null>(
    (current && days.some((d) => d.date === view.slots.find((x) => x.start === current)?.date)
      ? view.slots.find((x) => x.start === current)!.date
      : null) ?? firstFree,
  );
  const [slot, setSlot] = useState<string | null>(null);
  const [unitTypeId, setUnitTypeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const closeRef = useRef<HTMLButtonElement>(null);
  const { lang, S, P } = useTourWords();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [busy, onClose]);

  const title = P.sheetTitle[mode];
  const z = view.timeZone;
  const anyFree = days.some((d) => d.anyFree);

  async function run(action: () => Promise<PortalTourView>, message: string | null) {
    setBusy(true);
    setError('');
    try {
      onDone(await action(), message);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      setError(
        (code && P.errors[code]) ||
          (e instanceof ApiError && e.status === 0 ? (lang === 'he' ? e.message : P.failed) : P.failed),
      );
      if (
        code === 'TOUR_SLOT_TAKEN' ||
        code === 'TOUR_SLOT_UNAVAILABLE' ||
        code === 'TOUR_NOT_FOUND_UPCOMING' ||
        code === 'TOUR_CHANGES_CLOSED'
      ) {
        setSlot(null);
        api.myTour().then(onStale, () => undefined);
      }
    } finally {
      setBusy(false);
    }
  }

  const chosenLabel = slot ? `${tourDayLong(slot, z, lang)}${P.comma}${timeLabel(slot, z, S)}` : '';
  const cancelText = current
    ? P.cancelBody(tourDayLong(current, z, lang), timeLabel(current, z, S), !!view.tour?.canChange)
    : null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="tour-sheet-title">
      <div className="absolute inset-0 bg-black/40" onClick={() => !busy && onClose()} />
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[88vh] max-w-md flex-col rounded-t-2xl bg-white pb-[max(env(safe-area-inset-bottom),0.75rem)] shadow-2xl">
        <div className="flex items-center justify-between px-4 pt-3">
          <h2 id="tour-sheet-title" className="text-[19px] font-bold text-brand-900">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={P.close}
            className="flex h-12 w-12 items-center justify-center rounded-xl text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {mode === 'cancel' ? (
          <div className="px-4 pb-2 pt-1">
            {cancelText && (
              <p className="text-[16px] leading-relaxed text-slate-700">
                {cancelText[0]}
                <span dir="ltr">{cancelText[1]}</span>
                {cancelText[2]}
              </p>
            )}
            {error && <ErrorLine text={error} />}
            <div className="mt-5 grid gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => api.cancelTour(lang), null)}
                className="flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-red-600 px-4 text-[16px] font-semibold text-white disabled:opacity-60"
              >
                {busy && <Loader2 className="h-5 w-5 animate-spin" />} {P.cancelYes}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={onClose}
                className="min-h-[52px] rounded-xl border border-slate-200 bg-white px-4 text-[16px] font-semibold text-slate-700"
              >
                {P.cancelNo}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 pt-2">
              {mode === 'move' && current && (
                <p className="mb-4 text-[16px] text-slate-600">
                  {P.now} {tourDayLong(current, z, lang)}
                  {P.comma}
                  <span dir="ltr">{timeLabel(current, z, S)}</span>
                </p>
              )}
              {!anyFree ? (
                <div className="py-6 text-center">
                  <p className="text-[17px] font-bold text-brand-900">{S.noSlotsTitle}</p>
                  <p className="mt-1 text-[16px] leading-relaxed text-slate-600">{S.noSlotsBody}</p>
                </div>
              ) : (
                <>
                  {mode === 'book' && view.unitTypes.length > 0 && (
                    <fieldset className="mb-6 min-w-0">
                      <legend className="mb-2 text-[17px] font-bold text-brand-900">{P.unitQuestion}</legend>
                      <select
                        value={unitTypeId}
                        onChange={(e) => setUnitTypeId(e.target.value)}
                        aria-label={P.unitQuestion}
                        className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[16px] outline-none focus:border-brand-500"
                      >
                        <option value="">{`${S.unitTypeAny} (${S.optional})`}</option>
                        {view.unitTypes.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </fieldset>
                  )}
                  <SlotPicker
                    slots={view.slots}
                    timeZone={z}
                    s={S}
                    day={day}
                    onDay={(d) => {
                      setDay(d);
                      setSlot(null);
                    }}
                    onPick={setSlot}
                    selected={slot}
                    currentStart={mode === 'move' ? current : null}
                    currentLabel={P.currentLabel}
                  />
                </>
              )}
              {error && <ErrorLine text={error} />}
            </div>
            {anyFree && (
              <div className="border-t border-slate-100 px-4 pt-3">
                <button
                  type="button"
                  disabled={!slot || busy}
                  onClick={() =>
                    void run(
                      () =>
                        mode === 'book'
                          ? api.bookTour(slot!, unitTypeId || undefined, lang)
                          : api.rescheduleTour(slot!, lang),
                      mode === 'book' ? P.booked : P.moved,
                    )
                  }
                  className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[16px] font-semibold text-white disabled:bg-slate-200 disabled:text-slate-500"
                >
                  {busy && <Loader2 className="h-5 w-5 animate-spin" />}
                  {!slot ? P.pickTime : mode === 'book' ? P.bookFor(chosenLabel) : P.moveTo(chosenLabel)}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <p
      className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[16px] leading-relaxed text-red-700"
      role="alert"
    >
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      {text}
    </p>
  );
}
